import { useCallback, useEffect, useRef, useState } from "react";
import {
  getProduct,
  updateProduct,
  listProducts,
  getProductPricingModel,
  listItemPrices,
  listProductDeliverables,
  listProductProcessTemplates,
  getCatalogImageUrl,
  type Product,
  type PricingModel,
  type ProductDeliverable,
  type ProductProcessTemplateMapping,
  type BundleItem,
  type BundleBasePrice,
  BUNDLE_FAMILY_OPTIONS,
  SERVICE_OPERATION_TYPE_OPTIONS,
} from "../../../api/catalog";
import { resolveBundleComponentPricing } from "../../../api/bundlePricing";
import { cn } from "../../utils/cn";
import { Input } from "../../primitives/Input";
import { Select } from "../../primitives/Select";
import { Button } from "../../primitives/Button";
import { ProductAlerts } from "./ProductAlerts";
import { useProductAlerts } from "./useProductAlerts";
import { useCurrentRuleSet } from "../../../api/opsV2/hooks";
import { ChevronLeft, ChevronRight, ImageIcon, Package } from "lucide-react";
import { TagPillInput } from "../TagPillInput";
import { OpsFamilyCombobox } from "../OpsFamilyCombobox";

/** Gallery for product images with prev/next arrows when multiple images. */
function ProductImageGallery({ imagePaths }: { imagePaths: string[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasMultiple = imagePaths.length > 1;

  useEffect(() => {
    setCurrentIndex(0);
    setLoadError(false);
  }, [imagePaths]);

  // Ne charger l'image que lorsque la galerie est visible (évite de bloquer le chargement de la page)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setIsInView(entry?.isIntersecting ?? false),
      { rootMargin: "100px", threshold: 0.01 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  if (imagePaths.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-muted/20 h-48 text-muted gap-1">
        <span className="flex items-center gap-2 text-sm">
          <ImageIcon size={20} strokeWidth={1.5} />
          Aucune image
        </span>
        <span className="text-xs">Renseignez un chemin d&apos;image ci-dessous (ex. catalog_images/photo.jpg)</span>
      </div>
    );
  }

  const goPrev = () => setCurrentIndex((i) => (i <= 0 ? imagePaths.length - 1 : i - 1));
  const goNext = () => setCurrentIndex((i) => (i >= imagePaths.length - 1 ? 0 : i + 1));
  const currentUrl = getCatalogImageUrl(imagePaths[currentIndex]!);

  return (
    <div
      ref={containerRef}
      className="relative flex items-center justify-center rounded-lg border border-border bg-muted/10 overflow-hidden"
    >
      <div className="relative flex items-center justify-center w-full min-h-[240px] max-h-[320px] bg-surface">
        {isInView ? (
          <img
            src={currentUrl}
            alt=""
            className="max-w-full max-h-[320px] w-auto h-auto object-contain"
            loading="lazy"
            decoding="async"
            onError={() => setLoadError(true)}
            onLoad={() => setLoadError(false)}
          />
        ) : (
          <div className="flex items-center justify-center w-full min-h-[240px] text-muted" aria-hidden>
            <ImageIcon size={40} strokeWidth={1} />
          </div>
        )}
        {loadError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted/30 text-muted text-sm p-4 text-center">
            <ImageIcon size={32} strokeWidth={1.5} />
            <span>Image introuvable</span>
            <span className="text-xs">Vérifiez que le fichier existe dans static/ (ex. static/catalog_images/…)</span>
          </div>
        )}
        {hasMultiple && (
          <>
            <button
              type="button"
              onClick={goPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-bg/90 border border-border shadow-md hover:bg-surface-muted transition-colors"
              aria-label="Image précédente"
            >
              <ChevronLeft size={24} strokeWidth={2} className="text-text" />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-bg/90 border border-border shadow-md hover:bg-surface-muted transition-colors"
              aria-label="Image suivante"
            >
              <ChevronRight size={24} strokeWidth={2} className="text-text" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-bg/90 border border-border text-xs text-muted">
              {currentIndex + 1} / {imagePaths.length}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

type ProductGeneralTabProps = {
  productId: string;
  onProductUpdate?: (product: Product) => void;
  /** Suggested tag values (e.g. from other products) for the dropdown */
  suggestedTagValues?: string[];
  /** Suggested ops_family values for the combobox */
  suggestedOpsFamilyValues?: string[];
};

const BUNDLE_BASE_UNITS = [
  { value: "each", label: "Chaque" },
  { value: "acre", label: "Acre" },
  { value: "hectare", label: "Hectare" },
  { value: "project", label: "Projet" },
  { value: "hour", label: "Heure" },
];

const UNIT_KIND_OPTIONS = [
  { value: "each", label: "Chaque" },
  { value: "acre", label: "Acre" },
  { value: "hectare", label: "Hectare" },
  { value: "arpent", label: "Arpent" },
  { value: "gb_month", label: "Go/mois" },
  { value: "hour", label: "Heure" },
  { value: "project", label: "Projet" },
  { value: "percent", label: "Pourcentage" },
  { value: "credit", label: "Crédit" },
];

const TAX_CODE_OPTIONS = [
  { value: "OUT_OF_SCOPE", label: "Hors champ (0%)" },
  { value: "EXEMPT", label: "Exonéré (0%)" },
  { value: "ZERO_RATED", label: "Détaxé (0%)" },
  { value: "STANDARD_QC", label: "TPS/TVQ QC - 9,975 (14,975%)" },
];

function OrchestrationSection({ productId }: { productId: string }) {
  const { data: currentRuleSet, loading } = useCurrentRuleSet(productId);
  const hasRuleSet = !!currentRuleSet;
  return (
    <div className="rounded-md border border-border bg-muted/10 p-4">
      <h4 className="text-sm font-medium text-text border-b border-border pb-2">Orchestration</h4>
      {loading ? (
        <p className="text-xs text-muted">Chargement…</p>
      ) : (
        <>
          <p className="text-sm text-text">
            Ce produit déclenche des dossiers Ops V2 : <strong>{hasRuleSet ? "Oui" : "Non"}</strong>
          </p>
          <p className="text-xs text-muted mt-1">
            {hasRuleSet
              ? `Rule Set actif : ${currentRuleSet!.name ?? `v${currentRuleSet!.version_no}`}. Consultez l'onglet Orchestration pour les règles et le lien Studio.`
              : "Aucun Rule Set publié. Créez-en un dans le Studio et associez-le au produit (onglet Orchestration)."}
          </p>
        </>
      )}
    </div>
  );
}

export function ProductGeneralTab({
  productId,
  onProductUpdate,
  suggestedTagValues = [],
  suggestedOpsFamilyValues = [],
}: ProductGeneralTabProps) {
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    code: "",
    name: "",
    product_type: "produit" as Product["product_type"],
    status: "active" as Product["status"],
    track_inventory: false,
    description_md: "",
    unit_kind: "each",
    tags: [] as string[],
    tax_code: "STANDARD_QC",
    document_detail_template_md: "",
    service_metadata: null as Record<string, any> | null,
    bundle_base_price: null as BundleBasePrice | null,
    bundle_family: "" as string,
    service_operation_type: "" as string,
    ops_family: "" as string,
    image_path: "" as string,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pricingModel, setPricingModel] = useState<PricingModel | null>(null);
  const [itemPrices, setItemPrices] = useState<Array<{ price_type: string; unit_price: number }>>([]);
  const [deliverables, setDeliverables] = useState<ProductDeliverable[]>([]);
  const [processMappings, setProcessMappings] = useState<ProductProcessTemplateMapping[]>([]);
  const [bundleItems, setBundleItems] = useState<BundleItem[]>([]);
  const [catalogProducts, setCatalogProducts] = useState<Product[]>([]);
  const [bundleCatalogLoading, setBundleCatalogLoading] = useState(false);
  const [bundleModalOpen, setBundleModalOpen] = useState(false);
  const [editingBundleIndex, setEditingBundleIndex] = useState<number | null>(null);
  const [modalProductId, setModalProductId] = useState("");
  const [bundleModalSearch, setBundleModalSearch] = useState("");
  const [modalRequired, setModalRequired] = useState(true);
  const [modalCumulateOperational, setModalCumulateOperational] = useState(true);
  const [modalCumulateProcess, setModalCumulateProcess] = useState(true);
  const [modalCumulatePurchase, setModalCumulatePurchase] = useState(true);
  const [modalQuantity, setModalQuantity] = useState(1);
  const [bundleContentView, setBundleContentView] = useState<"list" | "listWithImages">("list");
  const [componentPricing, setComponentPricing] = useState<Record<string, { mode: string; base_rate: number }>>({});

  const { alerts, is_sellable } = useProductAlerts(
    product,
    null,
    pricingModel,
    itemPrices,
    deliverables,
    processMappings
  );

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    getProduct(productId)
      .then(async (data) => {
        if (!mounted) return;
        setProduct(data);
        const profile = (data as Product & { operational_cost_profile?: { bundle_items?: BundleItem[]; bundle_base_price?: BundleBasePrice | null } }).operational_cost_profile;
        setForm({
          code: data.code || "",
          name: data.name,
          product_type: data.product_type,
          status: data.status,
          track_inventory: (data as Product & { track_inventory?: boolean }).track_inventory ?? false,
          description_md: data.description_md || "",
          unit_kind: data.unit_kind || "each",
          tags: data.tags || [],
          tax_code: data.tax_code || "STANDARD_QC",
          document_detail_template_md: "",
          service_metadata: null,
          bundle_base_price: profile?.bundle_base_price ?? null,
          bundle_family: (data as Product & { bundle_family?: string | null }).bundle_family ?? "",
          service_operation_type: (data as Product & { service_operation_type?: string | null }).service_operation_type ?? "",
          ops_family: (data as Product & { ops_family?: string | null }).ops_family ?? "",
          image_path: data.image_path ?? "",
        });
        const [pm, prices, dels, procs] = await Promise.all([
          getProductPricingModel(data.id).catch(() => null),
          listItemPrices(data.id).catch(() => []),
          listProductDeliverables(data.id).catch(() => []),
          listProductProcessTemplates(data.id).catch(() => []),
        ]);
        if (!mounted) return;
        setPricingModel(pm ?? null);
        setItemPrices(prices.map((p) => ({ price_type: p.price_type, unit_price: p.unit_price })));
        setDeliverables(dels);
        setProcessMappings(procs);
        setBundleItems(Array.isArray(profile?.bundle_items) ? profile.bundle_items : []);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load product:", error);
        setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [productId]);

  const fetchBundleCatalogIfNeeded = useCallback(async () => {
    if (catalogProducts.length > 0) return;
    setBundleCatalogLoading(true);
    const PAGE_SIZE = 1000;
    try {
      const all: Product[] = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await listProducts({
          page,
          page_size: PAGE_SIZE,
          include_category_ids: true,
          list: true,
        });
        const items = res.items ?? [];
        all.push(...items as Product[]);
        const total = res.meta?.total ?? items.length;
        hasMore = all.length < total;
        page += 1;
      }
      setCatalogProducts(all);
    } catch {
      setCatalogProducts([]);
    } finally {
      setBundleCatalogLoading(false);
    }
  }, [catalogProducts.length]);

  // Preload catalog when bundle has items so we can show names, codes and images
  useEffect(() => {
    if ((form.product_type === "bundle" || form.product_type === "produit") && bundleItems.length > 0 && catalogProducts.length === 0) {
      fetchBundleCatalogIfNeeded();
    }
  }, [form.product_type, bundleItems.length, catalogProducts.length, fetchBundleCatalogIfNeeded]);

  // Load pricing for each bundle component (direct price or recursive sum for nested bundles)
  useEffect(() => {
    if (form.product_type !== "bundle" || bundleItems.length === 0) {
      setComponentPricing({});
      return;
    }
    const productIds = [...new Set(bundleItems.map((b) => b.product_id))];
    let cancelled = false;
    resolveBundleComponentPricing(productIds)
      .then((next) => {
        if (!cancelled) setComponentPricing(next);
      })
      .catch(() => {
        if (!cancelled) setComponentPricing({});
      });
    return () => {
      cancelled = true;
    };
  }, [form.product_type, bundleItems]);

  const handleSave = async () => {
    if (!product) return;

    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) {
      newErrors.name = "Le nom est requis";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    try {
      const profile = (product as Product).operational_cost_profile ?? {};
      const payload: Parameters<typeof updateProduct>[1] = {
        code: form.code || null,
        name: form.name,
        product_type: form.product_type,
        status: form.status,
        track_inventory: form.track_inventory,
        description_md: form.description_md || null,
        unit_kind: form.unit_kind || null,
        tags: form.tags.length > 0 ? form.tags : null,
        tax_code: form.tax_code,
        document_detail_template_md: form.document_detail_template_md || null,
        service_metadata: form.service_metadata || null,
        image_path: form.image_path.trim() || null,
        bundle_family: form.product_type === "bundle" && form.bundle_family ? form.bundle_family : null,
        service_operation_type: form.product_type === "service" && form.service_operation_type ? form.service_operation_type : null,
        ops_family: form.ops_family?.trim() || null,
      };
      if (form.product_type === "produit" || form.product_type === "bundle") {
        payload.operational_cost_profile = {
          ...profile,
          bundle_items: bundleItems,
          ...(form.product_type === "bundle" ? { bundle_base_price: form.bundle_base_price ?? undefined } : {}),
        };
      }
      const updated = await updateProduct(product.id, payload);
      setProduct(updated);
      setIsEditing(false);
      if (onProductUpdate) {
        onProductUpdate(updated);
      }
    } catch (error) {
      console.error("Failed to update product:", error);
      alert(`Erreur lors de la mise à jour: ${(error as Error).message || "Veuillez réessayer."}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (product) {
      const profile = (product as Product & { operational_cost_profile?: { bundle_items?: BundleItem[]; bundle_base_price?: BundleBasePrice | null } }).operational_cost_profile;
      setForm({
        code: product.code || "",
        name: product.name,
        product_type: product.product_type,
        status: product.status,
        track_inventory: (product as Product & { track_inventory?: boolean }).track_inventory ?? false,
        description_md: product.description_md || "",
        unit_kind: product.unit_kind || "each",
        tags: product.tags || [],
        tax_code: product.tax_code || "STANDARD_QC",
        document_detail_template_md: "",
        service_metadata: null,
        bundle_base_price: profile?.bundle_base_price ?? null,
        bundle_family: (product as Product & { bundle_family?: string | null }).bundle_family ?? "",
        service_operation_type: (product as Product & { service_operation_type?: string | null }).service_operation_type ?? "",
        ops_family: (product as Product & { ops_family?: string | null }).ops_family ?? "",
        image_path: (product as Product & { image_path?: string | null }).image_path ?? "",
      });
      setBundleItems(Array.isArray(profile?.bundle_items) ? profile.bundle_items : []);
    }
    setIsEditing(false);
    setErrors({});
  };

  const productNameById = Object.fromEntries(catalogProducts.map((p) => [p.id, p.name ?? p.code ?? p.id]));
  const productCodeById = Object.fromEntries(catalogProducts.map((p) => [p.id, p.code ?? ""]));
  const catalogProductById = Object.fromEntries(catalogProducts.map((p) => [p.id, p]));

  const formatCurrency = (n: number) => `${Number(n).toFixed(2)}\u00a0$`;
  const getBundleItemUnitPrice = (item: BundleItem): number | null => {
    const p = componentPricing[item.product_id];
    if (!p || p.base_rate <= 0) return null;
    return p.base_rate;
  };
  const getBundleItemLineTotal = (item: BundleItem): number | null => {
    const p = componentPricing[item.product_id];
    if (!p) return null;
    const qty = typeof item.quantity === "number" && item.quantity >= 0.01 ? item.quantity : 1;
    return p.base_rate * qty;
  };
  const bundleBaseAmount =
    form.bundle_base_price && typeof form.bundle_base_price.value === "number" ? form.bundle_base_price.value : 0;
  const bundleItemsSubtotal = bundleItems.reduce((sum, item) => sum + (getBundleItemLineTotal(item) ?? 0), 0);
  const bundleTotalBeforeTax = bundleBaseAmount + bundleItemsSubtotal;

  const availableForBundle = catalogProducts.filter(
    (p) => p.id !== productId && !bundleItems.some((b) => b.product_id === p.id)
  );
  const bundleModalFilteredList = availableForBundle.filter((p) => {
    const search = bundleModalSearch.trim().toLowerCase();
    if (!search) return true;
    const name = (p.name ?? "").toLowerCase();
    const code = (p.code ?? "").toLowerCase();
    return name.includes(search) || code.includes(search);
  });

  const openBundleModalForAdd = () => {
    setEditingBundleIndex(null);
    setModalProductId("");
    setBundleModalSearch("");
    setModalRequired(true);
    setModalQuantity(1);
    setModalCumulateOperational(true);
    setModalCumulateProcess(true);
    setModalCumulatePurchase(true);
    setBundleModalOpen(true);
    if (form.product_type === "produit" || form.product_type === "bundle") {
      fetchBundleCatalogIfNeeded();
    }
  };

  const openBundleModalForEdit = (index: number) => {
    const item = bundleItems[index];
    if (!item) return;
    setEditingBundleIndex(index);
    setModalProductId(item.product_id);
    setModalRequired(item.required);
    setModalQuantity(Math.max(1, Math.round(typeof item.quantity === "number" ? item.quantity : 1)));
    setModalCumulateOperational(item.cumulate_operational_cost);
    setModalCumulateProcess(item.cumulate_process_cost);
    setModalCumulatePurchase(item.cumulate_purchase_cost);
    setBundleModalOpen(true);
  };

  const closeBundleModal = () => {
    setBundleModalOpen(false);
    setEditingBundleIndex(null);
  };

  const submitBundleModal = () => {
    if (!modalProductId) return;
    const qty = Math.max(1, Math.round(Number(modalQuantity) || 1));
    const item: BundleItem = {
      product_id: modalProductId,
      quantity: qty,
      required: modalRequired,
      cumulate_operational_cost: modalCumulateOperational,
      cumulate_process_cost: modalCumulateProcess,
      cumulate_purchase_cost: modalCumulatePurchase,
    };
    let nextItems: BundleItem[];
    if (editingBundleIndex !== null) {
      nextItems = [...bundleItems];
      nextItems[editingBundleIndex] = item;
    } else {
      nextItems = [...bundleItems, item];
    }
    setBundleItems(nextItems);
    saveBundleItemsToBackend(nextItems);
    closeBundleModal();
  };

  const removeBundleItem = (index: number) => {
    setBundleItems((prev) => {
      const next = prev.filter((_, i) => i !== index);
      saveBundleItemsToBackend(next);
      return next;
    });
  };

  /** Persist bundle items to backend so they are not lost when changing tab/product. */
  const saveBundleItemsToBackend = useCallback(
    async (items: BundleItem[]) => {
      if (!product || (form.product_type !== "produit" && form.product_type !== "bundle")) return;
      const profile = (product as Product & { operational_cost_profile?: Record<string, unknown> }).operational_cost_profile ?? {};
      try {
        const updated = await updateProduct(product.id, {
          operational_cost_profile: {
            ...profile,
            bundle_items: items,
            ...(form.product_type === "bundle" ? { bundle_base_price: form.bundle_base_price ?? undefined } : {}),
          },
        });
        setProduct(updated);
        if (onProductUpdate) onProductUpdate(updated);
      } catch (err) {
        console.error("Failed to save bundle items:", err);
        alert(`Impossible d'enregistrer le contenu du bundle: ${(err as Error).message ?? "Veuillez réessayer."}`);
      }
    },
    [product, form.product_type, form.bundle_base_price, onProductUpdate]
  );

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted">Chargement...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted">Produit non trouvé</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Alerts */}
      <ProductAlerts alerts={alerts} is_sellable={is_sellable} />

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text">Informations générales</h3>
        {!isEditing && (
          <Button
            label="Modifier"
            intent="primary"
            size="sm"
            onClick={() => setIsEditing(true)}
          />
        )}
      </div>

      {/* Images du produit — galerie avec flèches gauche/droite si plusieurs images */}
      <div className="space-y-2">
        <span className="text-xs font-medium uppercase tracking-wider text-muted">Images</span>
        <ProductImageGallery
          imagePaths={
            isEditing
              ? form.image_path.trim() ? [form.image_path.trim()] : []
              : product.image_paths && product.image_paths.length > 0
                ? product.image_paths
                : product.image_path
                  ? [product.image_path]
                  : []
          }
        />
        {isEditing && (
          <Input
            label="Chemin image (optionnel)"
            placeholder="ex. catalog_images/photo.jpg ou nom_fichier.jpg"
            value={form.image_path}
            onChange={(e) => setForm({ ...form, image_path: e.target.value })}
          />
        )}
        {!isEditing && product.image_path && (
          <p className="text-xs text-muted">Chemin : {product.image_path}</p>
        )}
        {!product.image_path && !isEditing && (
          <p className="text-xs text-muted">Aucune image. Cliquez sur Modifier pour ajouter un chemin d&apos;image (fichier dans static/ côté backend).</p>
        )}
      </div>

      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Input
              label="Code produit"
              placeholder="SKU ou code"
              value={form.code}
              onChange={(e) => {
                setForm({ ...form, code: e.target.value });
                if (errors.code) setErrors({ ...errors, code: "" });
              }}
              disabled={!isEditing}
              state={errors.code ? "error" : "default"}
            />
            {errors.code && <p className="mt-1 text-xs text-danger">{errors.code}</p>}
          </div>

          <div>
            <Input
              label="Nom"
              placeholder="Nom du produit/service"
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value });
                if (errors.name) setErrors({ ...errors, name: "" });
              }}
              disabled={!isEditing}
              required
              state={errors.name ? "error" : "default"}
            />
            {errors.name && <p className="mt-1 text-xs text-danger">{errors.name}</p>}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-text mb-2">Statut</label>
            <Select
              options={["Actif", "Inactif", "Archivé"]}
              value={
                form.status === "active"
                  ? "Actif"
                  : form.status === "inactive"
                  ? "Inactif"
                  : "Archivé"
              }
              onChange={(value) => {
                const statusMap: Record<string, Product["status"]> = {
                  Actif: "active",
                  Inactif: "inactive",
                  Archivé: "archived",
                };
                setForm({ ...form, status: statusMap[value] || "active" });
              }}
              disabled={!isEditing}
            />
          </div>

          <div className="flex flex-col gap-1 pt-6">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="track_inventory"
                checked={form.track_inventory}
                onChange={(e) => setForm({ ...form, track_inventory: e.target.checked })}
                disabled={!isEditing || form.product_type === "bundle"}
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <label htmlFor="track_inventory" className="text-sm font-medium text-text cursor-pointer select-none">
                Suivi en inventaire
              </label>
            </div>
            {form.product_type === "bundle" && (
              <p className="text-xs text-muted">Les bundles ne sont pas suivis en inventaire.</p>
            )}
            {form.track_inventory && form.product_type !== "bundle" && (
              <p className="text-xs text-muted">Associez un fournisseur dans l&apos;onglet Approvisionnement pour les commandes.</p>
            )}
          </div>
        </div>
        {form.track_inventory && !isEditing && (
          <p className="text-xs text-muted">Ce produit est suivi en inventaire. Consultez l&apos;onglet Inventaire pour le stock et les mouvements.</p>
        )}

        {form.product_type === "service" && (
          <div className="rounded-md border border-border bg-surface-muted/30 p-4 space-y-2">
            <h4 className="text-sm font-medium text-text">Type de service</h4>
            <p className="text-xs text-muted">Typologie opérationnelle du service. Chaque type pourra être configuré (ex. mission drone → drone, caméra, résolution).</p>
            <Select
              options={SERVICE_OPERATION_TYPE_OPTIONS.map((o) => o.label)}
              value={SERVICE_OPERATION_TYPE_OPTIONS.find((o) => o.value === (form.service_operation_type || ""))?.label ?? "Non défini"}
              onChange={(value) => {
                const opt = SERVICE_OPERATION_TYPE_OPTIONS.find((o) => o.label === value);
                setForm({ ...form, service_operation_type: opt?.value ?? "" });
              }}
              disabled={!isEditing}
            />
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-text mb-2">Taxe</label>
            <Select
              options={TAX_CODE_OPTIONS.map((opt) => opt.label)}
              value={TAX_CODE_OPTIONS.find((opt) => opt.value === form.tax_code)?.label || ""}
              onChange={(value) => {
                const option = TAX_CODE_OPTIONS.find((opt) => opt.label === value);
                if (option) setForm({ ...form, tax_code: option.value });
              }}
              disabled={!isEditing}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-2">Unité</label>
            <Select
              options={UNIT_KIND_OPTIONS.map((opt) => opt.label)}
              value={UNIT_KIND_OPTIONS.find((opt) => opt.value === form.unit_kind)?.label || ""}
              onChange={(value) => {
                const option = UNIT_KIND_OPTIONS.find((opt) => opt.label === value);
                if (option) setForm({ ...form, unit_kind: option.value });
              }}
              disabled={!isEditing}
            />
          </div>

        </div>

        <div>
          <label className="block text-sm font-medium text-text mb-2">Description</label>
          <textarea
            className="w-full h-32 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
            placeholder="Description du produit/service (Markdown)"
            value={form.description_md}
            onChange={(e) => setForm({ ...form, description_md: e.target.value })}
            disabled={!isEditing}
          />
        </div>

        {/* Tags et Famille opérationnelle — sous la description, UX moderne (clic = mode ajout, filtre, Enter = ajouter/créer) */}
        <div className="space-y-4 pt-2 border-t border-border">
          <div>
            <label className="block text-sm font-medium text-text mb-2">Tags</label>
            <TagPillInput
              value={form.tags}
              onChange={(tags) => setForm({ ...form, tags })}
              suggestedValues={suggestedTagValues}
              placeholder="Cliquer pour ajouter un tag…"
              disabled={!isEditing}
              aria-label="Tags"
            />
            <p className="text-xs text-muted mt-1">Filtrer la liste en tapant ; Entrée pour ajouter ou créer un nouveau tag.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-2">Famille opérationnelle</label>
            <OpsFamilyCombobox
              value={form.ops_family || null}
              onChange={(v) => setForm({ ...form, ops_family: v ?? "" })}
              suggestedValues={suggestedOpsFamilyValues}
              placeholder="Cliquer pour choisir ou saisir…"
              disabled={!isEditing}
              aria-label="Famille opérationnelle"
            />
            <p className="text-xs text-muted mt-1">Même usage : filtrer, cliquer ou Entrée pour sélectionner ou créer une nouvelle valeur.</p>
          </div>
        </div>

        <OrchestrationSection productId={productId} />

        {(form.product_type === "produit" || form.product_type === "bundle") && (
          <div className="rounded-md border border-border bg-surface-muted/30 p-4 space-y-4">
            {form.product_type === "bundle" && (
              <>
              <div className="space-y-2 pb-4 border-b border-border">
                <h4 className="text-sm font-medium text-text">Famille de bundle</h4>
                <p className="text-xs text-muted">Typologie métier pour le filtrage et la présentation dans la soumission (devis).</p>
                <Select
                  options={BUNDLE_FAMILY_OPTIONS.map((o) => o.label)}
                  value={BUNDLE_FAMILY_OPTIONS.find((o) => o.value === (form.bundle_family || ""))?.label ?? "Sans famille"}
                  onChange={(value) => {
                    const opt = BUNDLE_FAMILY_OPTIONS.find((o) => o.label === value);
                    setForm({ ...form, bundle_family: opt?.value ?? "" });
                  }}
                  disabled={!isEditing}
                />
              </div>
              <div className="space-y-2 pb-4 border-b border-border">
                <h4 className="text-sm font-medium text-text">Prix de base du bundle (facultatif)</h4>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="bundle-has-base-gen"
                    checked={form.bundle_base_price != null}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        bundle_base_price: e.target.checked
                          ? { label: "Frais de dossier", mode: "fixed", value: 0, unit: "each" }
                          : null,
                      })
                    }
                    disabled={!isEditing}
                  />
                  <label htmlFor="bundle-has-base-gen" className="text-sm text-text">Ce bundle a un prix de base (ex. frais de dossier)</label>
                </div>
                {form.bundle_base_price != null && (
                  <div className="pl-6 space-y-2 border-l-2 border-border">
                    <Input
                      label="Libellé"
                      placeholder="ex. Frais de dossier"
                      value={form.bundle_base_price.label}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          bundle_base_price: form.bundle_base_price ? { ...form.bundle_base_price, label: e.target.value } : null,
                        })
                      }
                      disabled={!isEditing}
                    />
                    <div>
                      <label className="text-sm font-medium text-text mb-1 block">Mode</label>
                      <Select
                        options={["Prix fixe", "Par unité"]}
                        value={form.bundle_base_price.mode === "fixed" ? "Prix fixe" : "Par unité"}
                        onChange={(value) =>
                          setForm({
                            ...form,
                            bundle_base_price: form.bundle_base_price
                              ? { ...form.bundle_base_price, mode: value === "Par unité" ? "per_unit" : "fixed", unit: value === "Par unité" ? "each" : undefined }
                              : null,
                          })
                        }
                        disabled={!isEditing}
                      />
                    </div>
                    <Input
                      label={form.bundle_base_price.mode === "fixed" ? "Montant (CAD)" : "Montant par unité (CAD)"}
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.bundle_base_price.value === 0 ? "" : String(form.bundle_base_price.value)}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setForm({
                          ...form,
                          bundle_base_price: form.bundle_base_price ? { ...form.bundle_base_price, value: isNaN(v) ? 0 : v } : null,
                        });
                      }}
                      disabled={!isEditing}
                    />
                    {form.bundle_base_price.mode === "per_unit" && (
                      <div>
                        <label className="text-sm font-medium text-text mb-1 block">Unité</label>
                        <Select
                          options={BUNDLE_BASE_UNITS.map((o) => o.label)}
                          value={BUNDLE_BASE_UNITS.find((o) => o.value === (form.bundle_base_price?.unit ?? "each"))?.label ?? "Chaque"}
                          onChange={(value) => {
                            const opt = BUNDLE_BASE_UNITS.find((o) => o.label === value);
                            if (opt && form.bundle_base_price)
                              setForm({ ...form, bundle_base_price: { ...form.bundle_base_price, unit: opt.value } });
                          }}
                          disabled={!isEditing}
                        />
                      </div>
                    )}
                  </div>
                )}
                <p className="text-xs text-muted">
                  Le total du bundle = prix de base (si configuré) + somme des items obligatoires + somme des options choisies. Chaque item garde son mode de prix (fixe ou par unité).
                </p>
              </div>
              </>
            )}
            <h4 className="text-sm font-medium text-text border-b border-border pb-2">
              Contenu du bundle / forfait
            </h4>
            <p className="text-xs text-muted">
              Définissez les produits ou services inclus dans ce bundle. Les items obligatoires sont toujours inclus ; les optionnels peuvent être choisis par le client à la soumission. Indiquez pour chaque item si ses coûts (opérationnel, processus, achat) doivent être cumulés dans le calcul du bundle.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                label="Ajouter"
                intent="primary"
                size="sm"
                onClick={openBundleModalForAdd}
                disabled={!isEditing}
              />
              {isEditing && availableForBundle.length === 0 && catalogProducts.length > 1 && (
                <span className="text-xs text-muted">Tous les autres produits du catalogue sont déjà dans le bundle.</span>
              )}
            </div>
            {bundleItems.length === 0 ? (
              <p className="text-sm text-muted">Aucun item. Cliquez sur « Ajouter » pour inclure des produits ou services.</p>
            ) : (
              <div className="space-y-4">
                {/* Toggle Liste / Liste avec images */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted">Affichage :</span>
                  <div className="flex rounded-md border border-border overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setBundleContentView("list")}
                      className={cn(
                        "px-3 py-1.5 text-sm",
                        bundleContentView === "list"
                          ? "bg-primary text-white"
                          : "bg-surface text-muted hover:bg-muted/50"
                      )}
                    >
                      Liste
                    </button>
                    <button
                      type="button"
                      onClick={() => setBundleContentView("listWithImages")}
                      className={cn(
                        "px-3 py-1.5 text-sm",
                        bundleContentView === "listWithImages"
                          ? "bg-primary text-white"
                          : "bg-surface text-muted hover:bg-muted/50"
                      )}
                    >
                      Liste avec images
                    </button>
                  </div>
                </div>
                {(() => {
                  const withIndex = bundleItems.map((item, index) => ({ item, index }));
                  const required = withIndex.filter(({ item }) => item.required);
                  const optional = withIndex.filter(({ item }) => !item.required);
                  const productLabel = (pid: string) => {
                    const name = productNameById[pid] ?? pid;
                    const code = productCodeById[pid];
                    return { name, code: code ?? "" };
                  };
                  const productImagePath = (pid: string) => {
                    const p = catalogProductById[pid] as Product & { image_path?: string; image_paths?: string[] } | undefined;
                    if (!p) return null;
                    const path = p.image_paths?.[0] ?? p.image_path;
                    return path ? getCatalogImageUrl(path) : null;
                  };
                  const tableRow = ({ item, index }: { item: BundleItem; index: number }) => {
                    const { name, code } = productLabel(item.product_id);
                    const qty = typeof item.quantity === "number" && item.quantity >= 0.01 ? item.quantity : 1;
                    const unitPrice = getBundleItemUnitPrice(item);
                    const lineTotal = getBundleItemLineTotal(item);
                    return (
                      <tr key={`${item.product_id}-${index}`} className="border-b border-border/50 hover:bg-muted/20">
                        <td className="p-3 text-text">
                          <div>
                            <span className="font-medium">{name}</span>
                            {code && <span className="text-muted text-xs block mt-0.5">SKU : {code}</span>}
                          </div>
                        </td>
                        <td className="p-3 text-right tabular-nums whitespace-nowrap">{unitPrice != null ? formatCurrency(unitPrice) : "—"}</td>
                        <td className="p-3 text-center font-medium">{qty}</td>
                        <td className="p-3 text-right tabular-nums font-medium whitespace-nowrap">{lineTotal != null ? formatCurrency(lineTotal) : "—"}</td>
                        <td className="p-3 text-center">{item.cumulate_operational_cost ? "Oui" : "Non"}</td>
                        <td className="p-3 text-center">{item.cumulate_process_cost ? "Oui" : "Non"}</td>
                        <td className="p-3 text-center">{item.cumulate_purchase_cost ? "Oui" : "Non"}</td>
                        <td className="p-3">
                          <div className="flex gap-2">
                          <button type="button" onClick={() => openBundleModalForEdit(index)} className="text-sm text-accent hover:underline">Modifier</button>
                          <button type="button" onClick={() => removeBundleItem(index)} className="text-sm text-danger hover:underline">Retirer</button>
                          </div>
                        </td>
                      </tr>
                    );
                  };
                  const tableHead = (
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left p-3 font-medium text-text">Produit / service</th>
                      <th className="text-right p-3 font-medium text-text w-28 whitespace-nowrap">Prix unit.</th>
                      <th className="text-center p-3 font-medium text-text w-20">Quantité</th>
                      <th className="text-right p-3 font-medium text-text w-28 whitespace-nowrap">Montant</th>
                      <th className="text-left p-3 font-medium text-text text-center w-24">Coût opér.</th>
                      <th className="text-left p-3 font-medium text-text text-center w-24">Coût processus</th>
                      <th className="text-left p-3 font-medium text-text text-center w-24">Coût d&apos;achat</th>
                      <th className="text-left p-3 font-medium text-text w-28">Actions</th>
                    </tr>
                  );
                  const cardItem = ({ item, index }: { item: BundleItem; index: number }) => {
                    const { name, code } = productLabel(item.product_id);
                    const imgUrl = productImagePath(item.product_id);
                    const qty = typeof item.quantity === "number" && item.quantity >= 0.01 ? item.quantity : 1;
                    const unitPrice = getBundleItemUnitPrice(item);
                    const lineTotal = getBundleItemLineTotal(item);
                    return (
                      <div
                        key={`${item.product_id}-${index}`}
                        className="flex items-center gap-3 rounded-lg border border-border bg-surface p-3"
                      >
                        <div className="flex-shrink-0 w-12 h-12 rounded-md border border-border bg-muted/30 overflow-hidden flex items-center justify-center">
                          {imgUrl ? (
                            <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Package size={20} className="text-muted" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-text truncate">{name}</div>
                          {code && <div className="text-xs text-muted truncate">SKU : {code}</div>}
                          <div className="flex gap-3 mt-1 text-xs text-muted">
                            <span className="font-medium text-text">Qté : {qty}</span>
                            {unitPrice != null && <span className="tabular-nums">Unit. : {formatCurrency(unitPrice)}</span>}
                            {lineTotal != null && <span className="tabular-nums font-medium text-text">Montant : {formatCurrency(lineTotal)}</span>}
                            <span>Opér. : {item.cumulate_operational_cost ? "Oui" : "Non"}</span>
                            <span>Processus : {item.cumulate_process_cost ? "Oui" : "Non"}</span>
                            <span>Achat : {item.cumulate_purchase_cost ? "Oui" : "Non"}</span>
                          </div>
                        </div>
                        <div className="flex-shrink-0 flex flex-col items-end gap-1">
                          {lineTotal != null && <span className="text-sm font-medium tabular-nums whitespace-nowrap">{formatCurrency(lineTotal)}</span>}
                          <div className="flex gap-2">
                            <button type="button" onClick={() => openBundleModalForEdit(index)} className="text-sm text-accent hover:underline">Modifier</button>
                            <button type="button" onClick={() => removeBundleItem(index)} className="text-sm text-danger hover:underline">Retirer</button>
                          </div>
                        </div>
                      </div>
                    );
                  };
                  const bundleSummary = (
                    <div className="mt-4 p-4 rounded-lg border border-border bg-muted/20 space-y-2 max-w-md">
                      <h5 className="text-sm font-medium text-text">Récapitulatif avant taxes</h5>
                      {bundleBaseAmount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted">Prix de base du bundle</span>
                          <span className="tabular-nums font-medium">{formatCurrency(bundleBaseAmount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-muted">Total des items (prix × quantité)</span>
                        <span className="tabular-nums font-medium">{formatCurrency(bundleItemsSubtotal)}</span>
                      </div>
                      <div className="flex justify-between text-sm pt-2 border-t border-border font-medium text-text">
                        <span>Total avant taxes</span>
                        <span className="tabular-nums">{formatCurrency(bundleTotalBeforeTax)}</span>
                      </div>
                    </div>
                  );

                  if (bundleContentView === "listWithImages") {
                    return (
                      <>
                        {required.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-text mb-2">Items obligatoires</h5>
                            <div className="space-y-2">{required.map(({ item, index }) => cardItem({ item, index }))}</div>
                          </div>
                        )}
                        {optional.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium text-text mb-2">Items optionnels</h5>
                            <div className="space-y-2">{optional.map(({ item, index }) => cardItem({ item, index }))}</div>
                          </div>
                        )}
                        {bundleSummary}
                      </>
                    );
                  }
                  const requiredTotal = required.reduce((s, { item }) => s + (getBundleItemLineTotal(item) ?? 0), 0);
                  const optionalTotal = optional.reduce((s, { item }) => s + (getBundleItemLineTotal(item) ?? 0), 0);
                  const totalRow = (label: string, sum: number) => (
                    <tr className="border-t-2 border-border bg-muted/30 font-medium">
                      <td className="p-3 text-text" colSpan={3}>{label}</td>
                      <td className="p-3 text-right tabular-nums whitespace-nowrap">{formatCurrency(sum)}</td>
                      <td className="p-3" colSpan={4} />
                    </tr>
                  );
                  return (
                    <>
                      {required.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-text mb-2">Items obligatoires</h5>
                          <div className="overflow-x-auto rounded-md border border-border">
                            <table className="w-full text-sm">
                              <thead>{tableHead}</thead>
                              <tbody>{required.map(({ item, index }) => tableRow({ item, index }))}</tbody>
                              <tfoot>{totalRow("Total", requiredTotal)}</tfoot>
                            </table>
                          </div>
                        </div>
                      )}
                      {optional.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-text mb-2">Items optionnels</h5>
                          <div className="overflow-x-auto rounded-md border border-border">
                            <table className="w-full text-sm">
                              <thead>{tableHead}</thead>
                              <tbody>{optional.map(({ item, index }) => tableRow({ item, index }))}</tbody>
                              <tfoot>{totalRow("Total", optionalTotal)}</tfoot>
                            </table>
                          </div>
                        </div>
                      )}
                      {bundleSummary}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {bundleModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={closeBundleModal}
          >
            <div
              className="rounded-lg border border-border bg-surface shadow-lg w-full max-w-md flex flex-col max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-border">
                <h4 className="text-sm font-semibold text-text">
                  {editingBundleIndex !== null ? "Modifier l'item du bundle" : "Ajouter un item au bundle"}
                </h4>
                {editingBundleIndex === null && !modalProductId && (
                  <p className="text-xs text-muted mt-1">Recherchez puis sélectionnez un produit ou service dans la liste.</p>
                )}
              </div>

              {editingBundleIndex !== null ? (
                /* Mode édition : produit déjà fixé, formulaire uniquement */
                <div className="p-4 space-y-4 overflow-y-auto flex-1">
                  <div className="rounded-md bg-muted/30 px-3 py-2 text-sm text-text">
                    Produit sélectionné : <strong>{productNameById[modalProductId] ?? modalProductId}</strong>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text mb-1">Dans le bundle</label>
                    <select
                      value={modalRequired ? "required" : "optional"}
                      onChange={(e) => setModalRequired(e.target.value === "required")}
                      className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                    >
                      <option value="required">Obligatoire</option>
                      <option value="optional">Optionnel</option>
                    </select>
                    <p className="text-xs text-muted mt-1">Obligatoire = toujours inclus ; Optionnel = choisi par le client à la soumission.</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text mb-1">Quantité</label>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={modalQuantity}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setModalQuantity(Number.isNaN(v) || v < 1 ? 1 : v);
                      }}
                      className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                    />
                    <p className="text-xs text-muted mt-1">Nombre d&apos;unités de cet item dans le bundle. Impacte le calcul du prix (fixe ou par unité).</p>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-text">Cumuler les coûts de cet item lorsqu&apos;il est sélectionné</label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={modalCumulateOperational} onChange={(e) => setModalCumulateOperational(e.target.checked)} className="rounded border-border" />
                      <span className="text-sm text-text">Coût opérationnel (coûts internes par défaut de l&apos;item)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={modalCumulateProcess} onChange={(e) => setModalCumulateProcess(e.target.checked)} className="rounded border-border" />
                      <span className="text-sm text-text">Coût lié au processus (coûts ajoutés via le processus de l&apos;item)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={modalCumulatePurchase} onChange={(e) => setModalCumulatePurchase(e.target.checked)} className="rounded border-border" />
                      <span className="text-sm text-text">Coût d&apos;achat (fournisseur)</span>
                    </label>
                  </div>
                </div>
              ) : modalProductId ? (
                /* Mode ajout : produit déjà choisi, afficher le formulaire */
                <div className="p-4 space-y-4 overflow-y-auto flex-1">
                  <div className="flex items-center justify-between gap-2 rounded-md bg-muted/30 px-3 py-2">
                    <span className="text-sm text-text">
                      Produit sélectionné : <strong>{productNameById[modalProductId] ?? modalProductId}</strong>
                    </span>
                    <button
                      type="button"
                      onClick={() => setModalProductId("")}
                      className="text-xs text-accent hover:underline"
                    >
                      Changer
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text mb-1">Dans le bundle</label>
                    <select
                      value={modalRequired ? "required" : "optional"}
                      onChange={(e) => setModalRequired(e.target.value === "required")}
                      className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                    >
                      <option value="required">Obligatoire</option>
                      <option value="optional">Optionnel</option>
                    </select>
                    <p className="text-xs text-muted mt-1">Obligatoire = toujours inclus ; Optionnel = choisi par le client à la soumission.</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text mb-1">Quantité</label>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={modalQuantity}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setModalQuantity(Number.isNaN(v) || v < 1 ? 1 : v);
                      }}
                      className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                    />
                    <p className="text-xs text-muted mt-1">Nombre d&apos;unités de cet item dans le bundle. Impacte le calcul du prix (fixe ou par unité).</p>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-text">Cumuler les coûts de cet item lorsqu&apos;il est sélectionné</label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={modalCumulateOperational} onChange={(e) => setModalCumulateOperational(e.target.checked)} className="rounded border-border" />
                      <span className="text-sm text-text">Coût opérationnel (coûts internes par défaut de l&apos;item)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={modalCumulateProcess} onChange={(e) => setModalCumulateProcess(e.target.checked)} className="rounded border-border" />
                      <span className="text-sm text-text">Coût lié au processus (coûts ajoutés via le processus de l&apos;item)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={modalCumulatePurchase} onChange={(e) => setModalCumulatePurchase(e.target.checked)} className="rounded border-border" />
                      <span className="text-sm text-text">Coût d&apos;achat (fournisseur)</span>
                    </label>
                  </div>
                </div>
              ) : (
                /* Mode ajout : étape 1 = liste filtrable */
                <>
                  <div className="p-2 border-b border-border">
                    <input
                      type="text"
                      value={bundleModalSearch}
                      onChange={(e) => setBundleModalSearch(e.target.value)}
                      placeholder="Rechercher par nom ou code…"
                      className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-muted"
                      autoFocus
                    />
                  </div>
                  <div className="flex-1 overflow-y-auto min-h-0 p-2">
                    {bundleCatalogLoading && catalogProducts.length === 0 ? (
                      <p className="text-sm text-muted p-4 text-center">Chargement du catalogue…</p>
                    ) : bundleModalFilteredList.length === 0 ? (
                      <p className="text-sm text-muted p-4 text-center">
                        {bundleModalSearch.trim()
                          ? "Aucun produit ne correspond à la recherche."
                          : "Aucun autre produit à ajouter."}
                      </p>
                    ) : (
                      <ul className="space-y-0.5">
                        {bundleModalFilteredList.map((p) => (
                          <li key={p.id}>
                            <button
                              type="button"
                              onClick={() => setModalProductId(p.id)}
                              className="w-full text-left rounded-md px-3 py-2 hover:bg-muted/30 text-sm text-text flex items-center gap-2"
                            >
                              {p.code && <span className="text-muted font-mono text-xs">{p.code}</span>}
                              <span className="truncate">{p.name ?? p.code ?? p.id}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}

              <div className="p-4 border-t border-border flex justify-end gap-2">
                <Button label="Annuler" intent="ghost" size="sm" onClick={closeBundleModal} />
                {modalProductId && (
                  <Button
                    label={editingBundleIndex !== null ? "Enregistrer" : "Ajouter"}
                    intent="primary"
                    size="sm"
                    onClick={submitBundleModal}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {isEditing && (
          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button
              label="Annuler"
              intent="default"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
            />
            <Button
              label={isSaving ? "Enregistrement..." : "Enregistrer"}
              intent="primary"
              size="sm"
              onClick={handleSave}
              disabled={isSaving}
            />
          </div>
        )}
      </div>
    </div>
  );
}
